import { useState, useEffect, useCallback } from 'react';

const CONDITIONS = ['BRIGHT', 'BLACK'];
const FINISHES = ['', 'GRINDING', 'PEELED', 'DRAWN', 'CENTRELESS'];
const SHAPES = ['ROUND', 'SQUARE', 'HEX', 'FLAT'];
const BASES = [
  { id: 'INVOICE_DATE', label: 'From invoice date' },
  { id: 'EOM', label: 'From month end' },
];

const blankLine = () => ({
  grade: '', condition_type: 'BRIGHT', finish: '', tolerance: '',
  shape: 'ROUND', size_mm: '', qty_min: '', qty_max: '',
  uom: 'tons', period: 'MONTHLY',
});

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const sum = (rows, key) => rows.reduce((t, r) => t + (Number(r[key]) || 0), 0);

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const S = {
  card: { background: '#fff', border: '1px solid #e3e6ea', borderRadius: 8, padding: '16px 18px', marginBottom: 16 },
  metric: { background: '#f7f8fa', borderRadius: 6, padding: '10px 14px', flex: '1 1 160px' },
  metricLabel: { fontSize: 12, color: '#6b7280', marginBottom: 4 },
  metricValue: { fontSize: 22, fontWeight: 500, color: '#111827' },
  metricSub: { fontSize: 11, color: '#9ca3af' },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 12, fontWeight: 500, color: '#6b7280', background: '#f7f8fa', borderBottom: '1px solid #e3e6ea' },
  td: { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #f0f1f3' },
  input: { width: '100%', padding: '5px 7px', fontSize: 13, border: '1px solid #d7dbe0', borderRadius: 4, boxSizing: 'border-box' },
  btn: { padding: '6px 14px', fontSize: 13, border: '1px solid #c7ccd3', borderRadius: 5, background: '#fff', cursor: 'pointer' },
  btnPrimary: { padding: '6px 14px', fontSize: 13, border: '1px solid #1F3864', borderRadius: 5, background: '#1F3864', color: '#fff', cursor: 'pointer' },
  warn: { background: '#fff8e6', borderLeft: '3px solid #d99a1a', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#7a5606' },
  err: { background: '#fdecec', borderLeft: '3px solid #d33', padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#a32d2d' },
};

const CustomerRequirementsTab = ({ accountId }) => {
  const [req, setReq] = useState(null);
  const [lines, setLines] = useState([]);
  const [head, setHead] = useState({
    payment_term_days: '', payment_term_basis: 'INVOICE_DATE', grace_days: 0,
    total_qty_min: '', total_qty_max: '', uom: 'tons',
    review_frequency: 'MONTHLY', notes: '',
  });
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/customer-requirements/account/${accountId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Could not load requirements (${res.status})`);
      const data = await res.json();
      if (data) {
        setReq(data);
        setLines(data.items || []);
        setHead({
          payment_term_days: data.payment_term_days ?? '',
          payment_term_basis: data.payment_term_basis || 'INVOICE_DATE',
          grace_days: data.grace_days ?? 0,
          total_qty_min: data.total_qty_min ?? '',
          total_qty_max: data.total_qty_max ?? '',
          uom: data.uom || 'tons',
          review_frequency: data.review_frequency || 'MONTHLY',
          notes: data.notes || '',
        });
      } else {
        setReq(null);
        setLines([]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);
const setLine = (i, key, val) => {
    setLines((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setWarnings([]);
    const payload = {
      account_id: accountId,
      payment_term_days: num(head.payment_term_days),
      payment_term_basis: head.payment_term_basis,
      grace_days: Number(head.grace_days) || 0,
      total_qty_min: num(head.total_qty_min),
      total_qty_max: num(head.total_qty_max),
      uom: head.uom,
      review_frequency: head.review_frequency,
      notes: head.notes,
      items: lines.filter((l) => l.grade).map((l) => ({
        grade: l.grade,
        condition_type: l.condition_type,
        finish: l.finish || null,
        tolerance: l.tolerance || null,
        shape: l.shape || 'ROUND',
        size_mm: num(l.size_mm),
        length_mm: null,
        qty_min: num(l.qty_min),
        qty_max: num(l.qty_max),
        uom: l.uom || 'tons',
        period: l.period || 'MONTHLY',
        notes: null,
      })),
    };
    try {
      const url = req ? `/api/v1/customer-requirements/${req.id}` : '/api/v1/customer-requirements';
      const res = await fetch(url, {
        method: req ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Save failed (${res.status})`);
      setWarnings(data.warnings || []);
      setEditing(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const sMin = sum(lines, 'qty_min');
  const sMax = sum(lines, 'qty_max');
  const basisLabel = (BASES.find((b) => b.id === head.payment_term_basis) || {}).label || '';

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>Loading requirements...</div>;

  if (!req && !editing) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 14 }}>
          No monthly requirement recorded for this customer yet.
        </p>
        <button style={S.btnPrimary} onClick={() => { setLines([blankLine()]); setEditing(true); }}>
          Add requirement
        </button>
        {error && <div style={{ ...S.err, marginTop: 16, textAlign: 'left' }}>{error}</div>}
      </div>
    );
  }
return (
    <div style={{ padding: '4px 0' }}>
      {error && <div style={S.err}>{error}</div>}
      {warnings.map((w, i) => (<div key={i} style={S.warn}>{w}</div>))}

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 4, background: '#e6f4ea', color: '#1e6b39' }}>
              {req ? req.status : 'NEW'}
            </span>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              Effective from {req ? fmtDate(req.effective_from) : 'today'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing && <button style={S.btn} onClick={() => setEditing(true)}>Edit</button>}
            {editing && (
              <>
                <button style={S.btn} onClick={() => { setEditing(false); load(); }}>Cancel</button>
                <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={S.metric}>
            <div style={S.metricLabel}>Monthly tonnage</div>
            {editing ? (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input style={S.input} type="number" value={head.total_qty_min} onChange={(e) => setHead({ ...head, total_qty_min: e.target.value })} />
                <span style={{ color: '#9ca3af' }}>-</span>
                <input style={S.input} type="number" value={head.total_qty_max} onChange={(e) => setHead({ ...head, total_qty_max: e.target.value })} />
              </div>
            ) : (
              <div style={S.metricValue}>{head.total_qty_min || sMin} - {head.total_qty_max || sMax}</div>
            )}
            <div style={S.metricSub}>{head.uom}, lines total {sMin} - {sMax}</div>
          </div>

          <div style={S.metric}>
            <div style={S.metricLabel}>Payment cycle</div>
            {editing ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={S.input} type="number" placeholder="days" value={head.payment_term_days} onChange={(e) => setHead({ ...head, payment_term_days: e.target.value })} />
                <select style={S.input} value={head.payment_term_basis} onChange={(e) => setHead({ ...head, payment_term_basis: e.target.value })}>
                  {BASES.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </div>
            ) : (
              <div style={S.metricValue}>{head.payment_term_days || '-'} days</div>
            )}
            <div style={S.metricSub}>{basisLabel.toLowerCase()}{editing ? '' : ` · ${head.grace_days} day window`}</div>
          </div>

          <div style={S.metric}>
            <div style={S.metricLabel}>Grace days</div>
            {editing ? (
              <input style={S.input} type="number" value={head.grace_days} onChange={(e) => setHead({ ...head, grace_days: e.target.value })} />
            ) : (
              <div style={S.metricValue}>{head.grace_days}</div>
            )}
            <div style={S.metricSub}>reminders held until after this</div>
          </div>

          <div style={S.metric}>
            <div style={S.metricLabel}>Last reviewed</div>
            <div style={S.metricValue}>{req ? fmtDate(req.last_reviewed_on) : '-'}</div>
            <div style={S.metricSub}>{head.review_frequency.toLowerCase()} review</div>
          </div>
        </div>
      </div>

      <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid #e3e6ea' }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>Requirement lines</span>
          {editing && <button style={S.btn} onClick={() => setLines([...lines, blankLine()])}>Add line</button>}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={S.th}>Grade</th>
              <th style={S.th}>Condition</th>
              <th style={S.th}>Finish</th>
              <th style={S.th}>Tolerance</th>
              <th style={S.th}>Shape</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Size mm</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Min</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Max</th>
              {editing && <th style={S.th}></th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td style={S.td}>{editing ? <input style={S.input} value={l.grade || ''} onChange={(e) => setLine(i, 'grade', e.target.value)} /> : l.grade}</td>
                <td style={S.td}>{editing ? (
                  <select style={S.input} value={l.condition_type} onChange={(e) => setLine(i, 'condition_type', e.target.value)}>
                    {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>) : l.condition_type}</td>
                <td style={S.td}>{editing ? (
                  <select style={S.input} value={l.finish || ''} onChange={(e) => setLine(i, 'finish', e.target.value)}>
                    {FINISHES.map((f) => <option key={f} value={f}>{f || '-'}</option>)}
                  </select>) : (l.finish || '-')}</td>
                <td style={S.td}>{editing ? <input style={S.input} value={l.tolerance || ''} onChange={(e) => setLine(i, 'tolerance', e.target.value)} /> : (l.tolerance || '-')}</td>
                <td style={S.td}>{editing ? (
                  <select style={S.input} value={l.shape || 'ROUND'} onChange={(e) => setLine(i, 'shape', e.target.value)}>
                    {SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>) : l.shape}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{editing ? <input style={S.input} type="number" value={l.size_mm ?? ''} onChange={(e) => setLine(i, 'size_mm', e.target.value)} /> : Number(l.size_mm)}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{editing ? <input style={S.input} type="number" value={l.qty_min ?? ''} onChange={(e) => setLine(i, 'qty_min', e.target.value)} /> : Number(l.qty_min)}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{editing ? <input style={S.input} type="number" value={l.qty_max ?? ''} onChange={(e) => setLine(i, 'qty_max', e.target.value)} /> : Number(l.qty_max)}</td>
                {editing && (
                  <td style={{ ...S.td, textAlign: 'right' }}>
                    <button style={{ ...S.btn, padding: '3px 8px', color: '#a32d2d' }} onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
            <tr>
              <td style={{ ...S.td, fontWeight: 500 }} colSpan={6}>Total, {head.uom} per month</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 500 }}>{sMin}</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 500 }}>{sMax}</td>
              {editing && <td style={S.td}></td>}
            </tr>
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={S.card}>
          <div style={S.metricLabel}>Notes</div>
          <textarea style={{ ...S.input, minHeight: 60 }} value={head.notes} onChange={(e) => setHead({ ...head, notes: e.target.value })} />
        </div>
      )}

      {!editing && req && req.notes && (
        <div style={{ ...S.card, fontSize: 13, color: '#4b5563' }}>{req.notes}</div>
      )}
    </div>
  );
};

export default CustomerRequirementsTab;
